import { Link } from "react-router-dom";
import { mediaUrl } from "@/lib/api";
import { HlsVideo } from "@/components/media/HlsVideo";
import { FONTS, RATIO_CLASS } from "./blockConfig";
import { Gallery } from "./Gallery";

const textStyle = (b) => ({
  fontFamily: FONTS[b.font]?.css, fontSize: `${b.size}px`, color: b.color, fontWeight: b.weight,
  fontStyle: b.italic ? "italic" : "normal", textAlign: b.align, lineHeight: b.type === "heading" ? 1.05 : 1.6,
  letterSpacing: b.type === "heading" ? "-0.02em" : undefined,
});

const SmartLink = ({ to, className, children, testId, style }) => {
  if (!to) return <span className={className} style={style} data-testid={testId}>{children}</span>;
  return /^https?:\/\//.test(to) || to.startsWith("mailto:") || to.startsWith("tel:") ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={className} style={style} data-testid={testId}>{children}</a>
  ) : (
    <Link to={to} className={className} style={style} data-testid={testId}>{children}</Link>
  );
};

const EditableBlockText = ({ b, editing, onChange, Tag }) => (
  <Tag style={textStyle(b)} className={`whitespace-pre-line break-words ${editing ? "cursor-text outline-none" : ""}`}
    contentEditable={editing} suppressContentEditableWarning data-testid={`block-${b.id}-text`}
    onBlur={(e) => editing && onChange({ text: e.currentTarget.innerText })}>
    {b.text}
  </Tag>
);

const Placeholder = ({ children }) => (
  <div className="flex aspect-video w-full items-center justify-center border border-dashed border-black/25 bg-[#ECEAE6] text-sm text-[#57534E]">{children}</div>
);

export const BlockView = ({ b, editing, onChange }) => {
  const justify = { left: "justify-start", center: "justify-center", right: "justify-end" }[b.align] || "justify-start";
  switch (b.type) {
    case "heading":
      return <EditableBlockText b={b} editing={editing} onChange={onChange} Tag="h2" />;
    case "text":
      return <EditableBlockText b={b} editing={editing} onChange={onChange} Tag="p" />;
    case "image": {
      if (!b.media) return editing ? <Placeholder>Open settings to upload a photo</Placeholder> : null;
      const img = <img src={mediaUrl(b.media.id)} alt={b.caption || ""} className={`w-full ${RATIO_CLASS[b.ratio] ? `${RATIO_CLASS[b.ratio]} object-cover` : "h-auto"}`} style={{ borderRadius: `${b.radius}px` }} />;
      return (
        <figure>
          {b.link && !editing ? <SmartLink to={b.link} className="block">{img}</SmartLink> : img}
          {b.caption && <figcaption className="mt-2 text-sm text-[#57534E]" style={{ textAlign: b.align }}>{b.caption}</figcaption>}
        </figure>
      );
    }
    case "video":
      if (!b.media) return editing ? <Placeholder>Open settings to upload a video</Placeholder> : null;
      return (
        <HlsVideo key={`${b.media.id}-${b.autoplay}`} src={mediaUrl(b.media.id)} controls={b.controls} autoPlay={b.autoplay} muted={b.autoplay} loop={b.autoplay} playsInline
          className={`w-full bg-black ${RATIO_CLASS[b.ratio] ? `${RATIO_CLASS[b.ratio]} object-cover` : "h-auto"}`} />
      );
    case "gallery":
      return <Gallery block={b} editing={editing} onRemoveItem={(i) => onChange({ items: b.items.filter((_, j) => j !== i) })} />;
    case "button": {
      const cls = b.variant === "solid"
        ? "inline-flex items-center gap-2 px-7 py-3.5 text-base font-extrabold text-white transition-opacity hover:opacity-85"
        : "group inline-flex items-center gap-2 border-b-2 border-[#E62314] pb-1 text-lg font-extrabold transition-colors hover:text-[#E62314]";
      return (
        <div className={`flex ${justify}`}>
          <SmartLink to={editing ? "" : b.link} className={cls} testId={`block-${b.id}-button`} style={b.variant === "solid" ? { background: b.color } : { color: b.color }}>
            {b.label} →
          </SmartLink>
        </div>
      );
    }
    case "spacer":
      return <div style={{ height: `${b.height}px` }} className={editing ? "bg-[repeating-linear-gradient(45deg,transparent,transparent_6px,rgba(230,35,20,0.08)_6px,rgba(230,35,20,0.08)_12px)]" : ""} />;
    default:
      return null;
  }
};
